import ko from "knockout";
import Wrapup from "models/Wrapup";
import liveDataService from "services/liveDataService";
import ObservableModel from "util/ObservableModel";
import Contact from "models/outbound/Contact";
import Participant from "models/Participant";
import communicationMixin from "models/comunicationMixin";

/**
 * @constructor
 * @this Message
 * Observable representation of a message. In addition to the
 * public API fields, exposes an observable field 'active'
 * that indicates whether or not the messages is live. The message object that is returned
 * from the server is expected to have the following data:
 *
 * <pre><code>
 * {
 *     id: "1234a",
 *     selfuri: "http://inin.com/blah",
 *     participants: [
 *       {
 *           "id": 1233,
 *           "startTime": "2023-02-03T09:48:14.014Z",
 *           "connectedTime": "2023-02-03T09:48:14.029Z",
 *           "purpose": "customer",
 *           "direction": "inbound",
 *           "held": false,
 *            messages: [{
 *              state: "CONNECTING|ALERTING|CONNECTED|DISCONNECTED"
 *            }],
 *            userId: "passwordIsTaco",
 *            selfUri: "http://inin.com/foo"
 *       }
 *  ]
 * }
 * </code></pre>
 *
 * Participants on a message can have the following purpose values:
 *   workflow
 *   customer:
 *   agent:
 *   acd: The agent routing/matching engine for inbound/outbound messages
 *   ivr: The IVR/menu engine for inbound message
 *
 * @return {Message} a message
 */
const Message = ObservableModel.extend(
    {
        defaults: {
            maxParticipants: null,
            participants: [],
            wrapup: null,
        },
        modelProperties: {
            wrapup: Wrapup,
            contact: Contact,
            participants: Participant,
        },
    },
    function () {
        const self = this;

        self.interactionType = ko.observable("message");

        communicationMixin(self); //Adds functionality shared between different communication types.

        self.errorInfo = ko.computed(function () {
            if (
                self.participantForCurrentUser() &&
                self.participantForCurrentUser().errorInformation()
            ) {
                return self.participantForCurrentUser().errorInformation();
            }
            return null;
        });

        /**
         * Returns whether or not this message is a monitored message
         *
         * @return {Boolean} true if the message is monitored, otherwise false
         */
        self.isMonitor = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }
            return (
                self.participantForCurrentUser().monitoredParticipantId() !==
                null
            );
        });

        /**
         * A helper method to determine if this is a user message
         *
         * @return {Boolean} true if it is a user message, otherwise false.
         */
        self.isUserMessage = ko.computed(function () {
            if (self.participantForCurrentUser()) {
                return self.participantForCurrentUser().isUser();
            }
            return false;
        });

        /**
         * A helper method to determine if this is an agent message
         *
         * @return {Boolean} true if it is a agent message, otherwise false.
         */
        self.isAgentMessage = ko.computed(function () {
            if (self.participantForCurrentUser()) {
                return self.participantForCurrentUser().isAgent();
            }
            return false;
        });

        /**
         * A helper method to determine if the user's message is in an active status (i.e. a
         * a status where the user has not disconnected from the message).
         *
         * @return {Boolean} true if the message is in a connected status, false otherwise.
         */
        self.isActive = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }

            // message is active if status is in a connected state or the message is in
            // ACW and doesn't already have any wrapup saved for the current user
            return (
                !self.isDisconnected() ||
                (self.participantForCurrentUser().wrapupRequired() &&
                    !self.participantForCurrentUser().wrapup())
            );
        });
    }
);

liveDataService.registerTopic("messages", Message);

export default Message;
